import React from 'react'
import { useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion'
import { COLORS, FONTS, GLOW, GLASS } from '../shared/theme'
import { fadeIn, entrance, staggerDelay, breathe, pulse, SPRINGS } from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * ZoomText — Texto dramático estilo Netflix/title card.
 *
 * params: { title, subtitle, eyebrow, accent_color, style: 'zoom_in'|'zoom_out'|'split' }
 */
export default function ZoomText({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps, durationInFrames } = useVideoConfig()
  const {
    title = '',
    subtitle = '',
    eyebrow = '',
    accent_color = COLORS.accent.blue,
    style = 'zoom_in',
  } = params

  const titleWords = title.split(' ')
  const wordCount = titleWords.length
  const perWordDelay = 5
  const titleStartDelay = eyebrow ? 16 : 8

  const eyebrowOpacity = fadeIn(frame, 0, 15)
  const lineEntrance = entrance(frame, fps, eyebrow ? 12 : 4, SPRINGS.smooth)
  const subtitleOpacity = fadeIn(frame, titleStartDelay + wordCount * perWordDelay + 10, 20)
  const subtitleY = interpolate(
    frame,
    [
      titleStartDelay + wordCount * perWordDelay + 10,
      titleStartDelay + wordCount * perWordDelay + 30,
    ],
    [20, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  )

  const glowPulse = pulse(frame, 0.03, 0.6, 1.0)
  const titleBreath = breathe(frame, 0.02, 0.006)

  // Font size based on title length
  const titleFontSize =
    title.length <= 10
      ? 120
      : title.length <= 20
        ? 96
        : title.length <= 30
          ? 80
          : title.length <= 40
            ? 68
            : 56

  // Style-specific entrance for the entire title block
  const blockEntrance = entrance(frame, fps, eyebrow ? 12 : 6, SPRINGS.gentle)
  const blockScaleFrom = style === 'zoom_out' ? 0.7 : style === 'zoom_in' ? 1.18 : 1.0
  const blockScale =
    style === 'split'
      ? 1
      : interpolate(blockEntrance, [0, 1], [blockScaleFrom, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        })

  return (
    <SceneWrapper
      accent={accent_color}
      zoomFrom={1.0}
      zoomTo={1.02}
      texture={artDirection?.texture}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0,
          padding: '0 100px',
          textAlign: 'center',
        }}
      >
        {/* Eyebrow */}
        {eyebrow ? (
          <div
            style={{
              opacity: eyebrowOpacity,
              fontFamily: FONTS.mono,
              fontSize: 16,
              fontWeight: 600,
              color: accent_color,
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              marginBottom: 24,
              textShadow: `0 0 20px ${accent_color}50`,
            }}
          >
            {eyebrow}
          </div>
        ) : null}

        {/* Decorative line above title */}
        <div
          style={{
            width: interpolate(lineEntrance, [0, 1], [0, 80]),
            height: 2,
            background: `linear-gradient(90deg, transparent, ${accent_color}, transparent)`,
            marginBottom: 32,
            boxShadow: GLOW.sm(accent_color),
          }}
        />

        {/* Title — word by word or split */}
        {style === 'split' ? (
          <SplitTitle
            title={title}
            fontSize={titleFontSize}
            accent_color={accent_color}
            frame={frame}
            fps={fps}
            startDelay={titleStartDelay}
            glowPulse={glowPulse}
            titleBreath={titleBreath}
          />
        ) : (
          <div
            style={{
              transform: `scale(${blockScale * titleBreath})`,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: `0 ${titleFontSize * 0.28}px`,
              lineHeight: 1.05,
            }}
          >
            {titleWords.map((word, i) => {
              const delay = titleStartDelay + staggerDelay(i, perWordDelay)
              const wordOpacity = fadeIn(frame, delay, 14)
              const wordY = interpolate(frame, [delay, delay + 14], [28, 0], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              })
              return (
                <span
                  key={i}
                  style={{
                    opacity: wordOpacity,
                    transform: `translateY(${wordY}px)`,
                    display: 'inline-block',
                    fontFamily: FONTS.heading,
                    fontSize: titleFontSize,
                    fontWeight: 800,
                    color: COLORS.text.primary,
                    letterSpacing: '-0.03em',
                    textShadow:
                      i === 0
                        ? `0 0 60px ${accent_color}30, 0 2px 40px rgba(0,0,0,0.5)`
                        : '0 2px 40px rgba(0,0,0,0.5)',
                  }}
                >
                  {word}
                </span>
              )
            })}
          </div>
        )}

        {/* Decorative line below title */}
        <div
          style={{
            width: interpolate(lineEntrance, [0, 1], [0, 60]),
            height: 1,
            background: `rgba(255,255,255,0.1)`,
            marginTop: 32,
            marginBottom: subtitle ? 28 : 0,
          }}
        />

        {/* Subtitle */}
        {subtitle ? (
          <div
            style={{
              opacity: subtitleOpacity,
              transform: `translateY(${subtitleY}px)`,
              fontFamily: FONTS.body,
              fontSize: 28,
              fontWeight: 400,
              color: COLORS.text.secondary,
              letterSpacing: '0.04em',
              maxWidth: 800,
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </div>
        ) : null}

        {/* Accent dot */}
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: accent_color,
            marginTop: 40,
            boxShadow: GLOW.md(accent_color),
            opacity: glowPulse * fadeIn(frame, titleStartDelay + 10, 20),
          }}
        />
      </div>
    </SceneWrapper>
  )
}

/* ── Split style: title halves slide in from opposite sides ── */
function SplitTitle({
  title,
  fontSize,
  accent_color,
  frame,
  fps,
  startDelay,
  glowPulse,
  titleBreath,
}) {
  const words = title.split(' ')
  const mid = Math.ceil(words.length / 2)
  const leftHalf = words.slice(0, mid).join(' ')
  const rightHalf = words.slice(mid).join(' ')

  const leftEntrance = entrance(frame, fps, startDelay, SPRINGS.snappy)
  const rightEntrance = entrance(frame, fps, startDelay + 6, SPRINGS.snappy)

  const leftX = interpolate(leftEntrance, [0, 1], [-80, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
  const rightX = interpolate(rightEntrance, [0, 1], [80, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
  const leftOpacity = fadeIn(frame, startDelay, 16)
  const rightOpacity = fadeIn(frame, startDelay + 6, 16)

  return (
    <div
      style={{
        display: 'flex',
        gap: fontSize * 0.3,
        transform: `scale(${titleBreath})`,
        alignItems: 'baseline',
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}
    >
      <span
        style={{
          opacity: leftOpacity,
          transform: `translateX(${leftX}px)`,
          display: 'inline-block',
          fontFamily: FONTS.heading,
          fontSize,
          fontWeight: 800,
          color: COLORS.text.primary,
          letterSpacing: '-0.03em',
          textShadow: `0 0 60px ${accent_color}30`,
        }}
      >
        {leftHalf}
      </span>
      {rightHalf && (
        <span
          style={{
            opacity: rightOpacity,
            transform: `translateX(${rightX}px)`,
            display: 'inline-block',
            fontFamily: FONTS.heading,
            fontSize,
            fontWeight: 800,
            color: accent_color,
            letterSpacing: '-0.03em',
            textShadow: `0 0 60px ${accent_color}50`,
          }}
        >
          {rightHalf}
        </span>
      )}
    </div>
  )
}
